/**
 * Intercepts all fetch calls on /api/* URLs and redirects to sign-in
 * when the server returns 401 (unauthorized) or 302 (redirect to login).
 *
 * This catches expired session cookies that the browser still sends —
 * the server responds with 401/302 but the frontend otherwise thinks
 * everything is fine until a query fails silently.
 *
 * Call once at app bootstrap (in App.tsx). Safe to call multiple times.
 */

// Tracks whether the user is currently known to be authenticated.
// The interceptor only redirects when this is true — prevents the
// chicken-and-egg of redirecting during initial page load.
let authed = false;

export function setAuthedState(isAuthed: boolean) {
  authed = isAuthed;
}

/**
 * Bootstrap the fetch interceptor. Call once before any API calls are made.
 */
export function bootstrapSessionInterceptor() {
  const originalFetch = window.fetch;

  window.fetch = async function (
    ...args: Parameters<typeof fetch>
  ): Promise<Response> {
    const [resource] = args;
    const url =
      typeof resource === 'string'
        ? resource
        : resource instanceof URL
          ? resource.href
          : resource.url;

    // Only intercept /api/* URLs — leave everything else (CSS, JS, etc.) alone.
    if (typeof url !== 'string' || !url.startsWith('/api/')) {
      return originalFetch.apply(window, args);
    }

    const response = await originalFetch.apply(window, args);

    // Only redirect if the user is known to be authenticated (i.e., the initial
    // AuthContext check already succeeded). This prevents a redirect loop during
    // the initial page load before we know the session state.
    if (authed && (response.status === 401 || response.status === 302)) {
      window.location.href = '/sign-in';
    }

    return response;
  };
}
